import React, { useContext, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import classNames from "classnames";
import { Context, XtLocale, Cookie, Util, Hooks, Request, Storage } from "@xt/xtbase";
import { resolveCfdSymbol } from "../utils/getCfdSymbol";
import assistConfig from "../config/assistConfig";
import {
  buildAssetsMenu,
  buildProductNav,
  buildProfileMenu,
  DEPOSIT_HREF,
  PROFILE_LOGOUT_ICON,
} from "../config/productNav";
import GlobalHeaderLogo from "./Logo";
import NotificationBell from "./NotificationBell";
import UserAvatar from "./UserAvatar";
import IconCustomerService from "../img/icon/customer-service";
import { openLiveHelpCustomerService } from "../utils/customerService";
import { getAvatarList } from "../api/user";
import useSocialMediaLinks from "../hooks/useSocialMediaLinks";
import { getHelpCenterLink } from "../utils/helpCenterLink";
import {
  normalizeAvatarList,
  resolveUserAvatarUrl,
  resolveUserDisplayName,
  resolveUserEmailLabel,
} from "../utils/userAvatar";
import {
  IconBook,
  IconCheck,
  IconChevronDown,
  IconDeposit,
  IconGlobe,
  IconHelp,
  IconSearch,
} from "./icons";
import styles from "./index.module.less";

const { XtContext, XtAuth } = Context;
const { getUrl, getQuery } = Util;
const { useTranslation } = Hooks;
const { XtAxios, setHeader } = Request;

const DEFAULT_HIDDEN_LANG_KEYS = new Set(["zh-CN", "zh-HK"]);

function getUniqueLocaleList(hiddenLangKeys = DEFAULT_HIDDEN_LANG_KEYS) {
  const hidden = hiddenLangKeys instanceof Set ? hiddenLangKeys : new Set(hiddenLangKeys || []);
  const seen = new Set();
  return (XtLocale.localeList || []).filter((item) => {
    if (!item?.code || item.key === "default" || seen.has(item.code)) return false;
    if (hidden.has(item.key)) return false;
    seen.add(item.code);
    return true;
  });
}

function stripLocalePath(pathname, locale) {
  if (!pathname || !locale) return pathname || "/";
  const prefix = `/${locale}`;
  if (pathname === prefix) return "/";
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
  return pathname;
}

function CustomerServiceBtn({ t }) {
  const label = (() => {
    const v = t("footer.customer");
    return v && v !== "footer.customer" ? v : "Customer Support";
  })();

  return (
    <button
      type="button"
      aria-label={label}
      className={styles.utilityBtn}
      onClick={openLiveHelpCustomerService}
    >
      <IconCustomerService className={styles.utilityIconFilled} />
    </button>
  );
}

function HelpMenu({ t }) {
  const [open, setOpen] = useState(false);
  const socialLinks = useSocialMediaLinks();

  const helpCenterLabel = (() => {
    const v = t("footer.siteHelp");
    if (v && v !== "footer.siteHelp") return v;
    const legacy = t("nav.helpCenter");
    return legacy && legacy !== "nav.helpCenter" ? legacy : "Help Center";
  })();

  return (
    <div
      className={styles.helpWrap}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={(() => {
          const v = t("nav.helpSupport");
          return v && v !== "nav.helpSupport" ? v : "Help & support";
        })()}
        className={classNames(styles.utilityBtn, open && styles.utilityBtnOpen)}
        onClick={() => setOpen((v) => !v)}
      >
        <IconHelp className={styles.utilityIcon} />
      </button>
      {open && (
        <div className={styles.dropdownPanel}>
          <div className={classNames(styles.dropdownPanelInner, styles.helpPanelInner)}>
            <a
              key="help-center"
              href={getHelpCenterLink()}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.dropdownRow}
              onClick={() => setOpen(false)}
            >
              <span className={styles.dropdownRowIcon}>
                <IconBook />
              </span>
              <span className={styles.dropdownRowLabel}>{helpCenterLabel}</span>
            </a>
            {socialLinks.map((item) => (
              <a
                key={item.key}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.dropdownRow}
                onClick={() => setOpen(false)}
              >
                <span className={styles.dropdownRowIcon}>
                  {item.iconUrl ? (
                    <img src={item.iconUrl} alt="" width={16} height={16} referrerPolicy="no-referrer" />
                  ) : (
                    <span className={styles.helpSocialFallback}>{item.label.trim().charAt(0).toUpperCase()}</span>
                  )}
                </span>
                <span className={styles.dropdownRowLabel}>{item.label}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LangCurrencyMenu({ lang, currency, onLocale, onAssist, t, hiddenLangKeys }) {
  const [open, setOpen] = useState(false);
  const langAry = getUniqueLocaleList(hiddenLangKeys);

  return (
    <div
      className={styles.langWrap}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="Language"
        className={classNames(styles.utilityBtn, open && styles.utilityBtnOpen)}
        onClick={() => setOpen((v) => !v)}
      >
        <IconGlobe className={styles.utilityIcon} />
      </button>
      {open && (
        <div className={styles.langPanel}>
          <div className={styles.langPanelInner}>
            <span className={styles.langLabel}>{t("header.lang")}</span>
            {langAry.map((item) => (
              <button
                key={item.key}
                type="button"
                className={classNames(styles.langOption, lang === item.key && styles.langOptionActive)}
                onClick={() => {
                  onLocale(item);
                  setOpen(false);
                }}
              >
                <span>{item.lab}</span>
                {lang === item.key && <IconCheck className={styles.langCheck} />}
              </button>
            ))}
            <span className={styles.langLabel}>{t("header.currency")}</span>
            {assistConfig.map((item) => (
              <button
                key={item.key}
                type="button"
                className={classNames(
                  styles.langOption,
                  currency === item.key && styles.langOptionActive
                )}
                onClick={() => {
                  onAssist(item);
                  setOpen(false);
                }}
              >
                <span>{item.name}</span>
                {currency === item.key && <IconCheck className={styles.langCheck} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MegaMenuRow({ item, variant = "product", onClick }) {
  const Icon = item.icon;
  const iconNode =
    Icon &&
    (variant === "product" ? (
      <span className={styles.megaIcon}>
        <Icon />
      </span>
    ) : (
      <span className={styles.assetsMegaIcon}>
        <Icon />
      </span>
    ));

  return (
    <a
      href={item.href}
      className={classNames(styles.megaRow, variant === "assets" && styles.megaRowAssets)}
      onClick={onClick}
    >
      {iconNode}
      <span className={styles.megaText}>
        <span className={styles.megaLabel}>{item.label}</span>
        {item.desc && <span className={styles.megaDesc}>{item.desc}</span>}
      </span>
    </a>
  );
}

function AssetsMenu({ items, t }) {
  const [open, setOpen] = useState(false);
  const assetsLabel = (() => {
    const v = t("nav.assets");
    return v && v !== "nav.assets" ? v : "Assets";
  })();
  return (
    <div
      className={styles.dropdownWrap}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button type="button" className={styles.dropdownTrigger} onClick={() => setOpen((v) => !v)}>
        {assetsLabel}
        <IconChevronDown className={classNames(styles.chevron, open && styles.open)} />
      </button>
      {open && (
        <div className={styles.dropdownPanel}>
          <div className={classNames(styles.dropdownPanelInner, styles.assetsPanelInner)}>
            {items.map((item) => (
              <MegaMenuRow
                key={item.label}
                item={item}
                variant="assets"
                onClick={() => setOpen(false)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileMenu({ userInfo, items, onLogout, t }) {
  const [open, setOpen] = useState(false);
  const [avatarOptions, setAvatarOptions] = useState([]);

  const hasRemoteAvatar = !!`${userInfo?.avatarUrl || userInfo?.headImgUrl || ""}`.trim();

  useEffect(() => {
    if (!userInfo || hasRemoteAvatar) return;
    getAvatarList({ isAutoPopError: false })
      .then((data) => {
        setAvatarOptions(normalizeAvatarList(data));
      })
      .catch(() => {});
  }, [userInfo, hasRemoteAvatar]);

  const avatarUrl = resolveUserAvatarUrl(userInfo, avatarOptions);
  const displayName = resolveUserDisplayName(userInfo);
  const emailLabel = resolveUserEmailLabel(userInfo);

  return (
    <div
      className={styles.dropdownWrap}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button type="button" className={styles.profileBtn} onClick={() => setOpen((v) => !v)}>
        <UserAvatar src={avatarUrl} size={28} className={styles.profileAvatarImg} />
        <IconChevronDown className={classNames(styles.chevron, styles.profileChevron, open && styles.open)} />
      </button>
      {open && (
        <div className={styles.dropdownPanel}>
          <div className={classNames(styles.dropdownPanelInner, styles.profilePanelInner)}>
            <div className={styles.profilePanelHeader}>
              <UserAvatar src={avatarUrl} size={36} className={styles.profilePanelAvatarImg} />
              <div className={styles.profilePanelMeta}>
                <div className={styles.profilePanelName}>{displayName}</div>
                {emailLabel ? <div className={styles.profilePanelEmail}>{emailLabel}</div> : null}
              </div>
            </div>
            <div className={styles.profilePanelDivider} />
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.label}
                  href={item.href}
                  className={styles.profileMenuRow}
                  onClick={() => setOpen(false)}
                >
                  {Icon ? (
                    <span className={styles.profileMenuIcon}>
                      <Icon />
                    </span>
                  ) : null}
                  <span>{item.label}</span>
                </a>
              );
            })}
            <div className={styles.profilePanelDivider} />
            <span
              role="button"
              tabIndex={0}
              className={classNames(styles.profileMenuRow, styles.profileLogoutRow)}
              onClick={() => {
                onLogout();
                setOpen(false);
              }}
            >
              <span className={styles.profileMenuIcon}>
                <PROFILE_LOGOUT_ICON />
              </span>
              <span>{t("header.safelyLogout")}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function GlobalHeader({ mode = "fixed", hiddenLangKeys = DEFAULT_HIDDEN_LANG_KEYS }) {
  const router = useRouter();
  const t = useTranslation();
  const [appState, appDispatch] = useContext(XtContext);
  const { lang, currency, userInfo, loginStatus } = appState;
  const [openMenu, setOpenMenu] = useState(null);
  const [cfdSymbol, setCfdSymbol] = useState("btc_usdt_cfd");
  const [loginQuery, setLoginQuery] = useState("");

  useEffect(() => {
    resolveCfdSymbol({ loginStatus }).then(setCfdSymbol);
  }, [loginStatus]);

  useEffect(() => {
    const ref = getQuery("ref");
    setLoginQuery(`?backurl=${encodeURIComponent(location.href)}${ref ? `&ref=${ref}` : ""}`);
  }, [router.asPath]);

  const productNav = useMemo(
    () => buildProductNav({ getUrl, cfdSymbol, t }),
    [cfdSymbol, t]
  );
  const assetsMenu = useMemo(() => buildAssetsMenu({ getUrl, t }), [t]);
  const profileMenu = useMemo(() => buildProfileMenu({ t, getUrl }), [t]);

  const pathname = stripLocalePath(router.pathname, XtLocale.intl.locale);

  const isNavActive = (item) => {
    if (item.match && item.match.test(pathname)) return true;
    if (item.menu) {
      return item.menu.some((sub) => {
        try {
          const subPath = new URL(sub.href, "http://local").pathname;
          return pathname === subPath || pathname.startsWith(`${subPath}/`);
        } catch {
          return false;
        }
      });
    }
    try {
      const hrefPath = new URL(item.href, "http://local").pathname;
      return pathname === hrefPath || pathname.startsWith(`${hrefPath}/`);
    } catch {
      return false;
    }
  };

  const handleLangChange = (obj) => {
    if (obj.key === XtLocale.intl.locale) return;
    Cookie.set("lang", obj.code);
    Cookie.set("langManual", "1");
    setHeader({ lang: obj.code });
    appDispatch({ payload: { lang: obj.key } });
    const langPath = XtLocale.getLangPath();
    if (langPath) location.href = `/${obj.key}${XtLocale.getLeftPath()}`;
    else location.reload();
  };

  const handleAssistChange = (obj) => {
    Cookie.set("currency", obj.key);
    appDispatch({ payload: { currency: obj.key } });
  };

  const handleLogout = () => {
    XtAxios.get(Util.getOrigin("www") + "/uaapi/uaa/authorize/token/revoke")
      .then(() => {
        Storage.remove("token");
        Cookie.remove("token");
        Storage.remove("bindGoogleTip");
        Storage.remove("otcFiat");
        location.reload();
      })
      .catch(() => {})
      .finally(() => {});
  };

  return (
    <header className={classNames(styles.header, mode === "fixed" && styles.fixed)}>
      <div className={styles.inner}>
        <GlobalHeaderLogo />
        <span className={styles.divider} aria-hidden />

        <nav className={styles.productNav} aria-label={(() => {
          const v = t("nav.productNav");
          return v && v !== "nav.productNav" ? v : "Product";
        })()}>
          {productNav.map((item) => {
            const active = isNavActive(item);
            const hasMenu = !!item.menu?.length;
            return (
              <div
                key={item.id}
                className={styles.navItemWrap}
                onMouseEnter={() => hasMenu && setOpenMenu(item.id)}
                onMouseLeave={() => hasMenu && setOpenMenu(null)}
              >
                <a
                  href={item.href}
                  className={classNames(styles.navLink, active && styles.active)}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                  {item.featured && <span className={styles.featuredDot} aria-hidden />}
                  {hasMenu && (
                    <IconChevronDown
                      className={classNames(styles.chevron, openMenu === item.id && styles.open)}
                    />
                  )}
                  {item.pulse && !active && <span className={styles.pulseEmber} aria-hidden />}
                </a>
                {hasMenu && openMenu === item.id && (
                  <div className={styles.megaMenu}>
                    <div className={styles.megaPanel}>
                      {item.menu.map((sub) => (
                        <MegaMenuRow key={sub.label} item={sub} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={styles.rightCluster}>
          <a href={getUrl("/search")} className={styles.utilityBtn} aria-label={t("header.navSearch")}>
            <IconSearch className={styles.utilityIcon} />
          </a>

          <LangCurrencyMenu
            lang={lang}
            currency={currency}
            onLocale={handleLangChange}
            onAssist={handleAssistChange}
            hiddenLangKeys={hiddenLangKeys}
            t={t}
          />

          <HelpMenu t={t} />

          <XtAuth
            renderLogin={() => (
              <>
                <CustomerServiceBtn t={t} />
                <NotificationBell />
                <a href={getUrl(DEPOSIT_HREF)} className={styles.btnPrimary}>
                  <IconDeposit /> {(() => {
                    const v = t("home.deposit");
                    return v && v !== "home.deposit" ? v : "Deposit";
                  })()}
                </a>
                <AssetsMenu items={assetsMenu} t={t} />
                <ProfileMenu
                  userInfo={userInfo}
                  items={profileMenu}
                  onLogout={handleLogout}
                  t={t}
                />
              </>
            )}
            renderNotLogin={() => (
              <>
                <CustomerServiceBtn t={t} />
                <NotificationBell />
                <div className={styles.authCluster}>
                  <a href={getUrl(`/accounts/login${loginQuery}`)} className={styles.btnGhost}>
                    {t("header.login")}
                  </a>
                  <a href={getUrl(`/accounts/register${loginQuery}`)} className={styles.btnPrimary}>
                    {t("header.signUp")}
                  </a>
                </div>
              </>
            )}
          />
        </div>
      </div>
    </header>
  );
}
